export class CartView {
  private footerElement: HTMLElement;
  private noAudiencesElement: HTMLElement;
  private cartViewElement: HTMLElement;
  private nextOrderElement: HTMLElement | null;
  private nextReservedElement: HTMLElement | null;
  private errorElement: HTMLElement | null;
  private errorMessageElement: HTMLElement | null;
  private isExpanded: boolean = false;
  private toggleButton: HTMLElement | null = null;
  private toggleContent: HTMLElement | null = null;

  constructor() {
    this.footerElement = document.querySelector('.evenement-resume__footer') as HTMLElement;
    this.noAudiencesElement = document.querySelector(
      '.evenement-resume__no-audiences'
    ) as HTMLElement;
    this.cartViewElement = document.querySelector('[data-cart-view]') as HTMLElement;
    this.nextOrderElement = document.querySelector('.evenement-resume__next-order') as HTMLElement;
    this.nextReservedElement = document.querySelector('.evenement-resume__reserved') as HTMLElement;
    this.errorElement = document.querySelector('.evenement-resume__error') as HTMLElement;
    this.errorMessageElement = document.querySelector(
      '.evenement-resume__error-message'
    ) as HTMLElement;
  }

  showLoader(): void {
    this.nextOrderElement?.classList.add('is-loading');
    this.nextOrderElement?.classList.add('is-disabled');
    this.nextOrderElement?.setAttribute('disabled', 'disabled');
    this.nextReservedElement?.classList.add('is-loading');
    this.nextReservedElement?.classList.add('is-disabled');
    this.nextReservedElement?.setAttribute('disabled', 'disabled');
  }

  hideLoader(): void {
    this.nextOrderElement?.classList.remove('is-loading');
    this.nextOrderElement?.classList.remove('is-disabled');
    this.nextOrderElement?.removeAttribute('disabled');
    this.nextReservedElement?.classList.remove('is-loading');
    this.nextReservedElement?.classList.remove('is-disabled');
    this.nextReservedElement?.removeAttribute('disabled');
  }

  updateCart(items: any[], total: number): void {
    if (items.length === 0) {
      this.showEmptyState();
      this.removeNextOrderButton();
      return;
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    this.hideEmptyState();
    this.renderCart(items, total, totalQuantity);

    if (items.length >= 1 && !this.nextOrderElement) {
      this.addNextOrderButton(total);
    } else if (items.length >= 1) {
      this.updateNextOrderButton(total);
    }
  }

  private addNextOrderButton(total: number): void {
    this.nextOrderElement = document.createElement('button');
    this.nextOrderElement.className = 'evenement-resume__next-order';
    this.nextOrderElement.setAttribute('data-continue-order', '');
    this.nextOrderElement.innerHTML = `
            <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 6.5H36.3333C36.8856 6.5 37.3333 6.94771 37.3333 7.5V33.5C37.3333 34.0523 36.8856 34.5 36.3333 34.5H2C1.44771 34.5 1 34.0523 1 33.5V7.5C1 6.94772 1.44772 6.5 2 6.5Z" stroke="white" stroke-width="2"/>
                <rect x="21" y="19.8359" width="11.3333" height="8" rx="1" stroke="white" stroke-width="2"/>
                <path d="M38.3333 13.8359H0" stroke="white" stroke-width="2"/>
                <path d="M5 21.3359H16.6667M16.6667 26.3359H5" stroke="white" stroke-width="2"/>
            </svg>
            <div class="evenement-resume__submit">
                <span data-continue-order-text class="evenement-resume__total-amounts">${
                  total.toFixed(1) === '0.0'
                    ? 'Valider ma commande'
                    : `Continuer ma commande : ${total.toFixed(2)}€`
                }</span>
                <span class="evenement-resume__submit-loader"></span>
            </div>
        `;
    this.cartViewElement.insertAdjacentElement('afterend', this.nextOrderElement);
  }

  private updateNextOrderButton(total: number): void {
    if (this.nextOrderElement) {
      // if amount is 0, show "Valider ma commande"
      if (total == 0) {
        this.nextOrderElement.querySelector('[data-continue-order-text]')!.innerHTML = `
                Valider ma commande
            `;
      } else {
        this.nextOrderElement.querySelector('[data-continue-order-text]')!.innerHTML = `
                Continuer ma commande : ${total.toFixed(2)}€
            `;
      }
    }
  }

  private removeNextOrderButton(): void {
    if (this.nextOrderElement) {
      this.nextOrderElement.remove();
      this.nextOrderElement = null;
    }
  }

  private showEmptyState(): void {
    this.footerElement.innerHTML = `
            <div class="evenement-resume__no-audiences">
                Aucune place sélectionnée
            </div>
        `;
  }

  private hideEmptyState(): void {
    if (this.noAudiencesElement) {
      this.noAudiencesElement.style.display = 'none';
    }
  }

  private renderCart(items: any[], total: number, totalQuantity: number): void {
    const itemsHtml = items
      .map(
        item => `
            <div class="evenement-resume__item">
                <span class="evenement-resume__item-quantity">${item.quantity}x</span>
                <span class="evenement-resume__item-name">${item.name}</span>
                <span class="evenement-resume__item-price">${(item.price * item.quantity).toFixed(
                  2
                )}€</span>
            </div>
        `
      )
      .join('');

    this.footerElement.innerHTML = `
            <div class="evenement-resume__selection">
                <button class="evenement-resume__toggle ${
                  this.isExpanded ? 'is-expanded' : ''
                }" data-toggle-selection>
                    <span>Votre sélection</span>
                    <span class="evenement-resume__count">${totalQuantity} places</span>
                    <svg class="evenement-resume__toggle-icon" width="24" height="24" viewBox="0 0 24 24">
                        <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
                <div class="evenement-resume__content ${this.isExpanded ? 'is-expanded' : ''}">
                    <div class="evenement-resume__items">
                        ${itemsHtml}
                    </div>
                </div>
            </div>
        `;
  }

  initializeToggle(): void {
    document.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-toggle-selection]')) {
        this.isExpanded = !this.isExpanded;
        this.updateExpandedState();
      }
    });
  }

  private updateExpandedState(): void {
    const toggleButton = document.querySelector('.evenement-resume__toggle');
    const content = document.querySelector('.evenement-resume__content');

    if (toggleButton && content) {
      toggleButton.classList.toggle('is-expanded', this.isExpanded);
      content.classList.toggle('is-expanded', this.isExpanded);
    }
  }

  enablePaymentButton(): void {
    const paymentButton = document.querySelector('[data-pay-order]') as HTMLButtonElement;
    const resservedButton = document.querySelector('[data-reserved]') as HTMLButtonElement;
    const paymentContainer = document.querySelector('[data-validate-cart]');
    const paymentContainerReserved = document.querySelector('[data-validate-reserved]');
    if (paymentButton && paymentContainer && paymentContainerReserved) {
      paymentButton.removeAttribute('disabled');
      resservedButton.removeAttribute('disabled');
      paymentButton.classList.remove('btn--disabled');
      resservedButton.classList.remove('btn--disabled');
      paymentContainer.classList.remove('is-disabled');
      paymentContainerReserved.classList.remove('is-disabled');
    }
  }

  disablePaymentButton(): void {
    const paymentButton = document.querySelector('[data-pay-order]') as HTMLButtonElement;
    const resservedButton = document.querySelector('[data-reserved]') as HTMLButtonElement;
    const paymentContainer = document.querySelector('[data-validate-cart]');
    const paymentContainerReserved = document.querySelector('[data-validate-reserved]');
    if (paymentButton && paymentContainer && paymentContainerReserved) {
      paymentButton.setAttribute('disabled', 'disabled');
      resservedButton.setAttribute('disabled', 'disabled');
      paymentButton.classList.add('btn--disabled');
      resservedButton.classList.add('btn--disabled');
      paymentContainer.classList.add('is-disabled');
      paymentContainerReserved.classList.add('is-disabled');
    }
  }

  resetCart(): void {
    this.updateCart([], 0);
  }

  showError(message: string): void {
    if (this.errorElement && this.errorMessageElement) {
      this.errorMessageElement.textContent = message;
      this.errorElement.style.display = 'flex';

      // Scroll to error
      this.errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  hideError(): void {
    if (this.errorElement) {
      this.errorElement.style.display = 'none';
    }
  }
}
